import { dependencies } from '@pn/core/dependencies';
import type { Project } from '@pn/core/domain/project';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { findOrThrow } from '@pn/core/utils/logic';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { projectsSlice, type State } from './projectsSlice';

export const useProjectsStorage = () => {
  const { name, getInitialState } = projectsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    projects: useSliceSelector((state) => state.projects),
    selectedProject: useSliceSelector(selectSelectedProject),
    isProjectLoaded: useSliceSelector(
      (state) => state.projectIdLoaded === state.selectedProjectId
    ),
  };
};

export const projectsActions = () => {
  const { dispatch } = dependencies.store;
  const { actions } = projectsSlice;

  return {
    request: () => dispatch(actions.request()),
    add: (projectOrProjects: Project | Project[], upsert = true) =>
      dispatch(actions.add({ projectOrProjects, upsert })),
    error: () => dispatch(actions.error()),
    create: (project: Project) => dispatch(actions.create(project)),
    update: ({
      project,
      items,
    }: {
      project: Project;
      items: WorkspaceItem[];
    }) => dispatch(actions.update({ project, items })),
    remove: (projectId: Project['id']) => dispatch(actions.remove(projectId)),
    removeStackProjects: () => dispatch(actions.removeStackProjects()),
    share: (projectId: Project['id']) => dispatch(actions.share(projectId)),
    unshare: (projectId: Project['id']) => dispatch(actions.unshare(projectId)),
    select: (projectId: Project['id']) => dispatch(actions.select(projectId)),
    updateProjectIdLoaded: (projectId: Project['id']) =>
      dispatch(actions.updateProjectIdLoaded(projectId)),
  };
};

const selectSelectedProject = createSelector(
  [(state: State) => state.projects, (state: State) => state.selectedProjectId],
  (projects, selectedProjectId) =>
    findOrThrow(
      projects,
      (p) => p.id === selectedProjectId,
      'Selector failed: project not found'
    )
);
